// src/LogFileReader.js
import React, {
    useState,
    useEffect,
    useRef,
    useCallback,
  } from 'react';
  import axiosInstance      from './axiosInstance';
  import './styles/LogFileReader.css';
  import LogParsingInput    from './LogParsingInput';
  import ParsedResultsPopup from './ParsedResultsPopup';
  import ProtectedComponent from './ProtectedComponent';
  
  function LogFileReader() {
    /* ─────────────── state ─────────────── */
    const [folderPath]         = useState('./uploads');
    const [logFiles, setLogFiles]               = useState([]);
    const [selectedFiles, setSelectedFiles]     = useState(new Map());
    const [selectedFilesSize, setSelectedFilesSize] = useState(0);
    const [selectAllChecked, setSelectAllChecked]   = useState(false);
    const [error, setError]                     = useState('');
    const [fileFilter, setFileFilter]           = useState('zip');      // zip | .log | both
    const [progressLogs, setProgressLogs]       = useState([]);
    const [baseURL]                             = useState(
      process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:5000'
    );
    const [requestId, setRequestId]             = useState(null);
    const [keywords, setKeywords]               = useState([]);
    const [parsedResults, setParsedResults]     = useState([]);
    const [uploadedFiles, setUploadedFiles]     = useState([]);
    const [showPopup, setShowPopup]             = useState(false);
    const [showParsedResultsPopup, setShowParsedResultsPopup] = useState(false);
    const [showResultsLink, setShowResultsLink] = useState(false);
    const [currentPage, setCurrentPage]         = useState(0);
    const [pageSize]                = useState(50);
    const [predefinedSearch, setPredefinedSearch] = useState('Default Parse');
  
    const selectedFilesEndRef = useRef(null);
  
    /* ─────────────── list files ─────────────── */
    const handleCheckForFiles = useCallback(async () => {
      try {
        setError('');
        const { data } = await axiosInstance.get('/files', {
          params: { folderPath, filter: fileFilter },
        });
  
        const filtered = data.filter((file) => {
          if (fileFilter === 'zip')   return file.name.endsWith('.zip');
          if (fileFilter === '.log')  return file.name.endsWith('.log');
          return (
            file.name.endsWith('.zip') || file.name.endsWith('.log')
          ); // both
        });
  
        setLogFiles(filtered);
  
        if (filtered.length === 0) {
          const msg =
            fileFilter === 'zip'
              ? 'No ZIP files found in folder'
              : fileFilter === '.log'
                ? 'No LOG files found in folder'
                : 'No ZIP or LOG files found in folder';
          setError(msg);
        } else {
          setError('');
        }
      } catch (err) {
        console.error('Error fetching log files:', err);
        setError(`Error checking directory: ${err.message}`);
        setLogFiles([]);
      }
    }, [folderPath, fileFilter]);
  
    /* ─────────────── effects ─────────────── */
    //useEffect(() => handleCheckForFiles(), [fileFilter, handleCheckForFiles]);
  
     useEffect(() => {
           handleCheckForFiles();          // call it, ignore its Promise
         }, [fileFilter, handleCheckForFiles]);

    useEffect(() => {
      setSelectAllChecked(selectedFiles.size === logFiles.length);
    }, [selectedFiles, logFiles]);
  
    useEffect(() => {
      if (!requestId) return;
      const interval = setInterval(() => {
        axiosInstance
          .get(`${baseURL}/progress/${requestId}`)
          .then((res) => {
            setProgressLogs(res.data.logs);
            const processing = res.data.logs.filter(
              (l) => selectedFiles.has(l.file) && l.status === 'Processing...'
            );
            if (processing.length) {
              const last = processing[processing.length - 1].file;
              const el   = document.getElementById(last);
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'end' });
            }
            if (!processing.length) clearInterval(interval);
          });
      }, 500);
      return () => clearInterval(interval);
    }, [requestId, selectedFiles, baseURL]);
  
    /* ─────────────── handlers ─────────────── */
    const handleFileCheck = (file) => {
      const newMap = new Map(selectedFiles);
      let newSize  = selectedFilesSize;
      if (newMap.has(file.name)) {
        newMap.delete(file.name);
        newSize -= file.uncompressedSize;
      } else {
        newSize += file.uncompressedSize;
        if (newSize > 4 * 1024 * 1024 * 1024) {
          setError('Total selected file size exceeds 4 GB limit.');
          return;
        }
        newMap.set(file.name, file);
      }
      setSelectedFiles(newMap);
      setSelectedFilesSize(newSize);
      setError('');
    };
  
    const handleSelectAll = () => {
      let newMap  = new Map();
      let newSize = 0;
      if (!selectAllChecked) {
        for (const file of logFiles) {
          if (newSize + file.uncompressedSize > 4 * 1024 * 1024 * 1024) {
            setError('Total selected file size exceeds 4 GB limit.');
            break;
          }
          newMap.set(file.name, file);
          newSize += file.uncompressedSize;
        }
      }
      setSelectedFiles(newMap);
      setSelectedFilesSize(newSize);
      setSelectAllChecked(!selectAllChecked);
    };
  
    const handleDeleteFile = async (file) => {
      try {
        const encoded = encodeURIComponent(file);
        await axiosInstance.delete(`${baseURL}/files/${encoded}`);
        handleCheckForFiles();
      } catch (err) {
        console.error('Error deleting file:', err);
      }
    };
  
    const handleDrop = (e) => {
      e.preventDefault();
      const files    = Array.from(e.dataTransfer.files);
      const newFiles = files.filter(
        (f) => !uploadedFiles.some((u) => u.name === f.name)
      );
      setUploadedFiles((prev) => [...prev, ...newFiles]);
      setSelectedFiles((prev) => {
        const map = new Map(prev);
        newFiles.forEach((f) => map.set(f.name, f));
        return map;
      });
    };
  
    const handleDragOver = (e) => e.preventDefault();
  
    const handleFileUpload = (e) => {
      const files    = Array.from(e.target.files);
      const newFiles = files.filter(
        (f) => !uploadedFiles.some((u) => u.name === f.name)
      );
      setUploadedFiles((prev) => [...prev, ...newFiles]);
      setSelectedFiles((prev) => {
        const map = new Map(prev);
        newFiles.forEach((f) => map.set(f.name, f));
        return map;
      });
    };
  
    const handleUploadFiles = () => {
      const hasDup = uploadedFiles.some((f) => logFiles.includes(f.name));
      if (hasDup) {
        setShowPopup(true);
        setTimeout(() => setShowPopup(false), 3000);
        return;
      }
      if (!uploadedFiles.length) {
        setError('No files selected for upload.');
        return;
      }
      const form = new FormData();
      uploadedFiles.forEach((f) => form.append('files', f));
      axiosInstance
        .post(`${baseURL}/upload`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then(() => {
          setProgressLogs((prev) => [
            ...prev,
            ...uploadedFiles.map((f) => ({
              file: f.name,
              status: 'Successfully uploaded',
            })),
          ]);
          setUploadedFiles([]);
          handleCheckForFiles();
        })
        .catch((err) => console.error('Error uploading:', err));
    };
  
    const handleRemoveFileFromUploadList = (name) => {
      const newFiles = uploadedFiles.filter((f) => f.name !== name);
      setUploadedFiles(newFiles);
      setSelectedFiles((prev) => {
        const map = new Map(prev);
        map.delete(name);
        return map;
      });
    };
  
    const checkForDuplicates = (file) =>
      logFiles.includes(file.name);
  
    const handleRemoveKeyword = (kw) =>
      setKeywords(keywords.filter((k) => k !== kw));
  
    const handleProcessFiles = () => {
      if (!selectedFiles.size) {
        setError('Please select at least one file to process.');
        return;
      }
      const uniqueId  = Date.now().toString();
      const filesList = Array.from(selectedFiles.keys());
      setRequestId(uniqueId);
      axiosInstance
        .post(`${baseURL}/process-files`, {
          folderPath,
          selectedFiles: filesList,
          requestId: uniqueId,
          keywords,
          predefinedSearch,
          pageSize: 10,
        })
        .then((res) => {
          setProgressLogs(res.data.logs);
          const allResults = res.data.paginatedResults.flat();
          if (predefinedSearch === 'EPD Failure Parse') {
            setParsedResults(
              allResults.map((r) => r.matches).flat()
            );
          } else {
            const transformed = keywords.reduce((acc, kw) => {
              acc[kw] = {};
              allResults.forEach((fr) => {
                const matches = fr.matches.filter(
                  (m) => typeof m === 'string' && m.includes(kw)
                );
                if (matches.length) {
                  if (!acc[kw][fr.file]) acc[kw][fr.file] = [];
                  acc[kw][fr.file].push(...matches);
                }
              });
              return acc;
            }, {});
            setParsedResults(transformed);
          }
          handleCheckForFiles();
          setShowParsedResultsPopup(true);
          setShowResultsLink(true);
        })
        .catch((err) => {
          const msg =
            err.response?.data?.error || err.message || 'Error';
          console.error('Error processing files:', msg);
        });
    };
  
    const handleStartParsing = () => {
      if (
        predefinedSearch === 'EPD Failure Parse' ||
        (keywords.length && selectedFiles.size)
      ) {
        handleProcessFiles();
      } else {
        setError('Setup incomplete for parsing.');
      }
    };
  
    const handlePageChange = (idx) => setCurrentPage(idx);
  
    /* ─────────────── render ─────────────── */
    return (
      <ProtectedComponent functionCode="OP02" functionName="Log File Reader">
        <div className="LogFileReaderMainDiv">
        {/* Upload section */}
        <div className="uploadSection">
          <div
            className="dropZone"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
          >
            Drag &amp; Drop files here
            <input type="file" multiple onChange={handleFileUpload} />
            <div className="uploadedFilesList">
              <ul>
                {uploadedFiles.map((file, i) => (
                  <li
                    key={i}
                    className={checkForDuplicates(file) ? 'duplicate' : ''}
                  >
                    {file.name}{' '}
                    {checkForDuplicates(file) &&
                      '(File already exists in directory)'}
                    <button
                      onClick={() =>
                        handleRemoveFileFromUploadList(file.name)
                      }
                    >
                      -
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <button className="uploadButton" onClick={handleUploadFiles}>
            Upload
          </button>
          {showPopup && (
            <div className="popup">
              Upload disabled due to duplicate files. Please delete the file in
              the directory or remove it from the list to be uploaded.
            </div>
          )}
        </div>
  
        {/* Radio filter */}
        <div className="fileTypeFilters">
          <input
            type="radio"
            name="fileFilter"
            onChange={() => setFileFilter('zip')}
            checked={fileFilter === 'zip'}
          />{' '}
          Zip Files
          <input
            type="radio"
            name="fileFilter"
            onChange={() => setFileFilter('.log')}
            checked={fileFilter === '.log'}
          />{' '}
          Log Files
          <input
            type="radio"
            name="fileFilter"
            onChange={() => setFileFilter('both')}
            checked={fileFilter === 'both'}
          />{' '}
          Both
        </div>
  
        {/* Main content */}
        <div className="contentArea">
          <div className="fileLists">
            {/* Current files */}
            <div className="currentFiles">
              <h3>Files in the Directory:</h3>
              <h4>
                Select All{' '}
                <input
                  type="checkbox"
                  onChange={handleSelectAll}
                  checked={selectAllChecked}
                />
              </h4>
              {error && <div>{error}</div>}
  
              <table className="filesTable">
                <tbody>
                  {logFiles.map((file, i) => (
                    <tr key={i}>
                      <td
                        className={
                          uploadedFiles.some((u) => u.name === file.name)
                            ? 'duplicate'
                            : ''
                        }
                      >
                        {!uploadedFiles.some((u) => u.name === file.name) && (
                          <input
                            type="checkbox"
                            checked={selectedFiles.has(file.name)}
                            onChange={() => handleFileCheck(file)}
                          />
                        )}
                        {file.name}{' '}
                        <span className="fileSize">
                          ({(file.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                      </td>
                      <td>
                        <button onClick={() => handleDeleteFile(file.name)}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
  
            {/* Parsing controls */}
            <div className="logParsingInputContainer">
              <div className="dropdown-container">
                <label htmlFor="predefined-search">Search Type: </label>
                <select
                  id="predefined-search"
                  value={predefinedSearch}
                  onChange={(e) => setPredefinedSearch(e.target.value)}
                >
                  <option value="Default Parse">Default Parse</option>
                  <option value="EPD Failure Parse">EPD Failure Parse</option>
                </select>
              </div>
  
              {predefinedSearch === 'Default Parse' && (
                <>
                  <h4>Keywords to parse:</h4>
                  <div className="keywordsList">
                    <ul>
                      {keywords.map((k, i) => (
                        <li key={i}>
                          {k}{' '}
                          <button onClick={() => handleRemoveKeyword(k)}>
                            x
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <LogParsingInput onUpdate={setKeywords} />
                </>
              )}
  
              <button onClick={handleStartParsing}>Start Parsing</button>
            </div>
  
            {/* Selected files */}
            <div className="selectedFiles">
              <h3>Selected Files:</h3>
              <table className="selectedFilesTable">
                <tbody>
                  {Array.from(selectedFiles.values())
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((file, i) => (
                      <tr key={i} id={file.name}>
                        <td>
                          {file.name}{' '}
                          <span className="fileSize">
                            (Uncompressed:{' '}
                            {(file.uncompressedSize / 1024 / 1024).toFixed(2)}{' '}
                            MB)
                          </span>
                        </td>
                        <td>
                          {
                            progressLogs.find(
                              (l) => l.file === file.name
                            )?.status || ''
                          }
                        </td>
                      </tr>
                    ))}
                  <tr ref={selectedFilesEndRef} />
                </tbody>
              </table>
            </div>
          </div>
        </div>
  
        {/* Progress */}
        <div className="progressConsole">
          <h4>Progress Console:</h4>
          {progressLogs.length ? (
            <div className="progressLogs">
              {progressLogs.map((log, i) => (
                <div
                  key={i}
                  className={
                    log.status === 'Successfully uploaded' ? 'uploaded' : ''
                  }
                >
                  {log.file}: {log.status}
                </div>
              ))}
            </div>
          ) : (
            <div>No progress logs available</div>
          )}
        </div>
  
        {showResultsLink && (
          <div className="resultsLink">
            <button onClick={() => setShowParsedResultsPopup(true)}>
              Results
            </button>
          </div>
        )}
  
        {showParsedResultsPopup && (
          <ParsedResultsPopup
            parsedResults={parsedResults}
            onClose={() => setShowParsedResultsPopup(false)}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            predefinedSearch={predefinedSearch}
          />
        )}
      </div>
      </ProtectedComponent>
    );
  }

  export default LogFileReader;
  